import {ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {MsfSelect} from '@components/select/select';
import {
  Era5DayNormalizedWeatherService,
  Era5MonthWeatherReportService,
  IbmMonthWeatherReportService
} from '@6grain/masofa-monolith-api-client/weather-report';
import {UgmWeatherDataService} from '@6grain/masofa-monolith-api-client/weather';
import {YearMultiPickerComponent} from '@components/date-picker';
import {SvgChartComponent, ChartData, ChartDataset, ChartConfig} from '@components/svg-chart';
import {
  WeatherNormDataManager,
  MultiYearDataManager,
  WeatherIndicatorManager,
  Era5DataManager,
  UgmDataManager,
  IbmDataManager,
  MultiIndicatorChartDataManager
} from './managers';
import {MsfPChartComponent} from '@components/msf-p-chart/msf-p-chart.component';
import {MsfMultiSelect} from '@components/multi-select/multi-select';

@Component({
  selector: 'app-weather-graph-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, MsfSelect, YearMultiPickerComponent, MsfPChartComponent, MsfMultiSelect, SvgChartComponent],
  templateUrl: './weather-graph-tab.component.html',
  styleUrls: ['./weather-graph-tab.component.scss']
})
export class WeatherGraphTabComponent implements OnChanges {
  private era5MonthWeatherService = inject(Era5MonthWeatherReportService);
  private era5NormalizedService = inject(Era5DayNormalizedWeatherService);
  private ugmWeatherDataService = inject(UgmWeatherDataService);
  private ibmMonthWeatherReportService = inject(IbmMonthWeatherReportService);
  private translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  // @Input() weatherStationId: string | null = null;
  @Input() dataSourceOptions?: any[];
  @Input() latitude?: number | null = null;
  @Input() longitude?: number | null = null;
  @Input() date?: string | Date = new Date()

  chartData: ChartData = {labels: [], datasets: []};
  chartConfig: ChartConfig = {
    showGridLines: true,
    yAxisUnit: '',
    viewBox: {width: 800, height: 500},
    padding: {top: 50, right: 50, bottom: 100, left: 50},
    chartHeight: 350,
    gridLineCount: 13,
    lineStrokeWidth: 2,
    scales: {
      x: {
        stacked: false
      },
      y: {
        stacked: false
      }
    }
  };
  isLoading: boolean = false;
  selectedIndicator: string = 'temperature';
  selectedIndicators: string[] = ['temperature'];
  previousSelectedIndicators: string[] = ['temperature', 'precipitation'];
  selectedPeriod: string = new Date().getFullYear().toString();
  selectedDataSource: string = 'era5';
  yAxisValues: number[] = [0, 25, 50, 75, 100];
  errorMessage: string | null = null;

  selectedYear: Date | null = null;

  // Multi-year selection
  selectedYears: number[] = [];

  // Chart type selection
  chartType: 'line' | 'bar' = 'line';
  chartTypeOptions = [
    { label: 'Line', value: 'line' },
    { label: 'Bar', value: 'bar' }
  ];

  // Managers
  private indicatorManager!: WeatherIndicatorManager;
  private era5DataManager!: Era5DataManager;
  private ugmDataManager!: UgmDataManager;
  private ibmDataManager!: IbmDataManager;
  private weatherNormDataManager!: WeatherNormDataManager;
  private multiYearDataManager!: MultiYearDataManager;
  private multiIndicatorChartDataManager!: MultiIndicatorChartDataManager;

  constructor() {
    // Initialize with current year
    const currentYear = new Date().getFullYear();
    this.selectedYear = new Date(currentYear, 0, 1);
    this.selectedPeriod = currentYear.toString();
    this.selectedYears = [currentYear];

    // Initialize managers
    this.indicatorManager = new WeatherIndicatorManager(this.translate);
    this.weatherNormDataManager = new WeatherNormDataManager(this.translate);
    this.multiYearDataManager = new MultiYearDataManager(this.translate);

    // Initialize data managers
    this.era5DataManager = new Era5DataManager(
      this.era5MonthWeatherService,
      this.era5NormalizedService,
      this.indicatorManager
    );
    this.ugmDataManager = new UgmDataManager(this.ugmWeatherDataService, this.indicatorManager);
    this.ibmDataManager = new IbmDataManager(
      this.ibmMonthWeatherReportService,
      this.indicatorManager
    );

    // Initialize multi-indicator chart manager
    this.multiIndicatorChartDataManager = new MultiIndicatorChartDataManager(
      this.translate,
      this.indicatorManager,
      this.weatherNormDataManager
    );

  }

  indicatorsOptions = [
    {label: 'Temperature', value: 'temperature'},
    {label: 'Fallout', value: 'precipitation'},
    {label: 'Humidity', value: 'humidity'},
    {label: 'WIND_SPEED', value: 'windSpeed'},
    {label: 'SolarRadiationInfluence', value: 'solarRadiation'}
  ];

  ngOnChanges(changes: SimpleChanges): void {

    if ((changes['latitude'] || changes['longitude']) &&
      this.latitude !== null && this.longitude !== null) {
      // For UGM/IBM, we can load data without weatherStationId
      if (this.selectedDataSource === 'ugm' || this.selectedDataSource === 'ibm' || this.selectedDataSource === 'era5') {
        this.loadChartData();
      }
    }
  }

  onDataSourceChange(): void {
    this.loadChartData();
  }

  /**
   * Handle multi-year selection change
   */
  onYearsChange(years: number[]): void {
    this.selectedYears = years;
    if (years.length > 0) {
      // Update selectedPeriod to the first (most recent) year for backwards compatibility
      this.selectedPeriod = years[0].toString();
      this.loadChartData();
    }
  }

  /**
   * Check if multiple years are selected
   */
  isMultiYearMode(): boolean {
    return this.selectedYears.length > 1;
  }

  /**
   * Load chart data for the selected period and indicator
   */
  private loadChartData(): void {
    this.onIndicatorChanges();
  }

  /**
   * Aggregate normalized data by month
   */
  private aggregateNormDataByMonth(normalizedData: any[], indicator?: string): Map<number, any> {
    return this.era5DataManager.aggregateNormalizedDataByMonth(normalizedData, indicator || this.selectedIndicator);
  }

  /**
   * Get translated month names
   */
  private getTranslatedMonthNames(): string[] {
    return [
      this.translate.instant('MONTH_JAN'),
      this.translate.instant('MONTH_FEB'),
      this.translate.instant('MONTH_MAR'),
      this.translate.instant('MONTH_APR'),
      this.translate.instant('MONTH_MAY'),
      this.translate.instant('MONTH_JUN'),
      this.translate.instant('MONTH_JUL'),
      this.translate.instant('MONTH_AUG'),
      this.translate.instant('MONTH_SEP'),
      this.translate.instant('MONTH_OCT'),
      this.translate.instant('MONTH_NOV'),
      this.translate.instant('MONTH_DEC')
    ];
  }

  /**
   * Get value for specific indicator from weather data
   */
  private getIndicatorValue(data: any, indicator: string): number {
    const ind = (indicator || '').toLowerCase();

    const toNumber = (v: any, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    // Temperature
    if (ind.includes('temp') || ind.includes('temperature')) {
      return toNumber(data.temperatureAverage ?? data.temperature ?? data.temperatureMin ?? data.temperatureMax ?? data.temperatureValue ?? 0);
    }

    // Precipitation / fallout
    if (ind.includes('precip') || ind.includes('fall') || ind.includes('fallout') || ind.includes('rain')) {
      return toNumber(data.fallout ?? data.precipitation ?? data.precipitationAmount ?? 0);
    }

    // Humidity
    if (ind.includes('humid')) {
      return toNumber(data.humidity ?? 0);
    }

    // Wind speed
    if (ind.includes('wind')) {
      return toNumber(data.windSpeed ?? data.wind_speed ?? 0);
    }

    // Solar radiation
    if (ind.includes('solar') || ind.includes('radiation')) {
      const rawValue = toNumber(data.solarRadiationInfluence ?? data.solarRadiation ?? 0);
        const source = data.solarRadiationInfluence !== null && data.solarRadiationInfluence !== undefined
        ? 'era5'  // solarRadiationInfluence is ERA5 field (normalized or actual ERA5 data)
        : (this.selectedDataSource === 'ugm' ? 'ugm' : (this.selectedDataSource === 'ibm' ? 'ibm' : 'era5'));
      return this.normalizeSolarRadiation(rawValue, source);
    }

    return 0;
  }

  /**
   * Get color for indicator
   */
  private getIndicatorColor(indicator: string): string {
    return this.indicatorManager.getColor(indicator);
  }

  /**
   * Normalize solar radiation value based on data source
   */
  private normalizeSolarRadiation(value: number | null | undefined, source: string): number {
    return this.indicatorManager.normalizeSolarRadiation(value, source);
  }

  /**
   * Update Y-axis values (alias for calculateYAxisValues)
   */
  private updateYAxisValues(): void {
    this.calculateYAxisValues();
  }

  calculateYAxisValues(): void {
    // Update the yAxisUnit in chartConfig
    this.chartConfig = {
      ...this.chartConfig,
      yAxisUnit: this.getYAxisUnit()
    };

    if (!this.chartData.datasets || this.chartData.datasets.length === 0) {
      this.yAxisValues = [0, 25, 50, 75, 100];
      return;
    }

    const allValues = this.chartData.datasets
      .flatMap(d => d.data)
      .filter(v => v !== null && v !== undefined) as number[];

    if (allValues.length === 0) {
      this.yAxisValues = [0, 25, 50, 75, 100];
      return;
    }

    const max = Math.max(...allValues);
    let min = Math.min(...allValues);

    if (min > 0) min = 0;

    const range = max - min;
    const step = range / 5;

    const isDecimal = max % 1 !== 0;

    this.yAxisValues = Array.from({length: 6}, (_, i) => {
      const value = max - (i * step);
      return isDecimal ? Number(value.toFixed(2)) : Math.round(value);
    });
  }

  getYAxisUnit(): string {
    const indicator = this.selectedIndicator?.toLowerCase();
    if (indicator?.includes('temperature')) return '°C';
    if (indicator?.includes('precipitation')) return this.translate.instant('gis.unit.mm');
    if (indicator?.includes('humidity')) return '%';
    if (indicator?.includes('wind')) return this.translate.instant('gis.unit.ms');
    if (indicator?.includes('solar')) return this.translate.instant('gis.unit.kcal');
    return '';
  }

  /**
   * Get unit for a specific indicator
   */
  private getUnitForIndicator(indicator: string): string {
    const ind = indicator?.toLowerCase();
    if (ind?.includes('temperature')) return '°C';
    if (ind?.includes('precipitation')) return this.translate.instant('gis.unit.mm');
    if (ind?.includes('humidity')) return '%';
    if (ind?.includes('wind')) return this.translate.instant('gis.unit.ms');
    if (ind?.includes('solar')) return this.translate.instant('gis.unit.kcal');
    return '';
  }

  /**
   * Handle multi-indicator selection change
   */
  onIndicatorChanges(): void {
    this.chartData = {labels: [], datasets: []};

    if (this.selectedIndicators.length > 0) {

      // Update previous indicators
      this.previousSelectedIndicators = [...this.selectedIndicators];

      // Reload chart data to rebuild datasets with correct axes
      this.loadChartDataForIndicators();
    }
  }

  /**
   * Load chart data for multiple selected indicators
   */
  private loadChartDataForIndicators(): void {
    this.errorMessage = null;

    // Check coordinates if needed
    if ((this.selectedDataSource === 'ugm' || this.selectedDataSource === 'ibm' || this.selectedDataSource === 'era5') &&
      (!this.latitude || !this.longitude)) {
      this.chartData = {labels: [], datasets: []};
      this.isLoading = false;
      this.errorMessage = this.translate.instant('COORDINATES_REQUIRED') ||
        'Координаты обязательны для построения графика.';
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    // Load data based on data source
    switch (this.selectedDataSource) {
      case 'era5':
        this.loadEra5DataForMultipleIndicators();
        break;
      case 'ugm':
        this.loadUgmDataForMultipleIndicators();
        break;
      case 'ibm':
        this.loadIbmDataForMultipleIndicators();
        break;
      default:
        this.loadEra5DataForMultipleIndicators();
    }
  }

  /**
   * Load ERA5 data for multiple indicators
   */
  private loadEra5DataForMultipleIndicators(): void {
    // If multi-year mode, load data for all selected years
    if (this.isMultiYearMode()) {
      this.loadMultiYearEra5DataForMultipleIndicators();
      return;
    }

    this.era5DataManager.loadDataWithNormalized({
      year: this.selectedPeriod,
      latitude: this.latitude,
      longitude: this.longitude
    }).subscribe(result => {
      this.isLoading = false;
      if (result.monthlyData && result.monthlyData.length > 0) {
        this.processMultiIndicatorChartData(result.monthlyData, result.normalizedData ?? null);
      } else {
        this.processMultiIndicatorChartData(result.monthlyData ?? [], result.normalizedData ?? null);
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * Load ERA5 multi-year data for multiple indicators
   */
  private loadMultiYearEra5DataForMultipleIndicators(): void {
    this.era5DataManager.loadMultiYearData({
      years: this.selectedYears,
      latitude: this.latitude,
      longitude: this.longitude
    }).subscribe(result => {
      this.isLoading = false;
      this.processMultiIndicatorMultiYearData(result.yearDataArrays, result.normalizedData ?? null);
      this.cdr.markForCheck();
    });
  }

  /**
   * Load UGM data for multiple indicators
   */
  private loadUgmDataForMultipleIndicators(): void {
    if (!this.latitude || !this.longitude) {
      console.error('Latitude or longitude missing for UGM data');
      this.isLoading = false;
      return;
    }

    this.ugmDataManager.loadDailyData({
      latitude: this.latitude,
      longitude: this.longitude,
      days: 7
    }).subscribe({
      next: (result) => {
        this.isLoading = false;
        // For UGM daily data, we'll process each indicator separately
        this.processMultiIndicatorDailyData(result.data, result.dates);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading UGM daily data:', error);
        this.isLoading = false;
        this.chartData = {labels: [], datasets: []};
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load IBM data for multiple indicators
   */
  private loadIbmDataForMultipleIndicators(): void {
    if (this.latitude == null || this.longitude == null) {
      console.error('Latitude or longitude missing for IBM data');
      this.isLoading = false;
      return;
    }
    const latitude = this.latitude;
    const longitude = this.longitude;

    // If multi-year mode, load data for all selected years
    if (this.isMultiYearMode()) {
      this.loadMultiYearIbmDataForMultipleIndicators();
      return;
    }

    const selectedYear = parseInt(this.selectedPeriod);

    this.ibmDataManager.loadMonthlyData({
      latitude,
      longitude,
      year: selectedYear
    }).subscribe({
      next: (monthlyData) => {
        if (monthlyData && monthlyData.length > 0) {
          this.era5DataManager.loadNormalizedData(latitude, longitude).subscribe(normalizedData => {
            this.isLoading = false;
            this.processMultiIndicatorIbmChartData(monthlyData, normalizedData ?? null);
            this.cdr.markForCheck();
          });
        } else {
          this.isLoading = false;
          this.chartData = {labels: [], datasets: []};
          this.updateYAxisValues();
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error loading IBM monthly data:', error);
        this.isLoading = false;
        this.chartData = {labels: [], datasets: []};
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load IBM multi-year data for multiple indicators
   */
  private loadMultiYearIbmDataForMultipleIndicators(): void {
    if (this.latitude == null || this.longitude == null) {
      console.error('Latitude or longitude missing for IBM multi-year data');
      this.isLoading = false;
      return;
    }
    const latitude = this.latitude;
    const longitude = this.longitude;

    this.ibmDataManager.loadMultiYearData({
      latitude,
      longitude,
      years: this.selectedYears
    }).subscribe({
      next: (result) => {
        this.era5DataManager.loadNormalizedData(latitude, longitude).subscribe(normalizedData => {
          this.isLoading = false;
          this.processMultiIndicatorMultiYearIbmData(result.yearDataArrays, normalizedData ?? null);
          this.cdr.markForCheck();
        });
      },
      error: (error) => {
        console.error('Error loading IBM multi-year data:', error);
        this.isLoading = false;
        this.chartData = {labels: [], datasets: []};
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Process multi-indicator chart data for ERA5 single year
   */
  private processMultiIndicatorChartData(monthlyData: any[], normalizedData: any[] | null): void {
    // Always prepare chart for selected year even if monthlyData is empty
    if (!this.selectedIndicators || this.selectedIndicators.length === 0) {
      this.chartData = {labels: [], datasets: []};
      this.calculateYAxisValues();
      return;
    }

    const selectedYear = parseInt(this.selectedPeriod);
    const maxMonth = this.era5DataManager.getMaxMonth(selectedYear);
    const monthNames = this.getTranslatedMonthNames();
    const labels = monthNames.slice(0, maxMonth);

    // Prepare dataByIndicator: if we have monthlyData -> extract real values, otherwise fill with nulls
    const dataByIndicator = new Map<string, (number | null)[]>();
    if (monthlyData && monthlyData.length > 0) {
      const dataByMonth = this.era5DataManager.processMonthlyDataByMonth(monthlyData);
      this.selectedIndicators.forEach(indicator => {
        const monthlyValues = this.era5DataManager.extractMonthlyValues(dataByMonth, maxMonth, indicator);
        dataByIndicator.set(indicator, monthlyValues);
      });
    } else {
      const emptyValues: (number | null)[] = Array.from({length: maxMonth}, () => null);
      this.selectedIndicators.forEach(indicator => {
        dataByIndicator.set(indicator, [...emptyValues]);
      });
    }

    // Extract normalized values for each indicator when available
    let normDataByIndicator: Map<string, (number | null)[]> = new Map();
    if (normalizedData && normalizedData.length > 0) {
      // populate the map
      this.selectedIndicators.forEach(indicator => {
        const normDataByMonth = this.aggregateNormDataByMonth(normalizedData, indicator);
        const normValues: (number | null)[] = [];
        for (let month = 1; month <= maxMonth; month++) {
          const normMonth = normDataByMonth.get(month);
          if (normMonth) {
            const normValue = normMonth.average !== undefined
              ? normMonth.average
              : this.getIndicatorValue(normMonth, indicator);
            normValues.push(normValue);
          } else {
            normValues.push(null);
          }
        }
        normDataByIndicator.set(indicator, normValues);
      });
    }

    // Create datasets using the multi-indicator manager
    const datasets = this.multiIndicatorChartDataManager.createMultiIndicatorDatasets(
      this.selectedIndicators,
      dataByIndicator,
      normDataByIndicator
    );

    // Ensure legend clearly shows Actual data [Year] for actual datasets and keep Norm label for norm datasets
    const yearLabel = String(selectedYear);
    datasets.forEach(ds => {
      // norm datasets created by WeatherNormDataManager include '(Norm)' in label
      if (!ds.label?.includes(this.translate.instant('Norm'))) {
        // Append selected year to actual data label when it's not already present
        if (ds.label && !/\(\d{4}\)$/.test(ds.label)) {
          ds.label = `${ds.label} (${yearLabel})`;
        }
      }
    });

    this.chartData = {labels, datasets};
    this.configureMultiAxisChart();
  }

  /**
   * Process multi-indicator data for daily (UGM)
   */
  private processMultiIndicatorDailyData(data: any[], dates: Date[] | string[]): void {
    if (!data || data.length === 0 || this.selectedIndicators.length === 0) {
      this.chartData = {labels: [], datasets: []};
      this.calculateYAxisValues();
      return;
    }

    // Convert dates to strings if they are Date objects
    const dateLabels = dates.map(d => typeof d === 'string' ? d : (d as Date).toLocaleDateString());

    // Extract values for each indicator
    const dataByIndicator = new Map<string, (number | null)[]>();
    this.selectedIndicators.forEach(indicator => {
      const values = data.map(d => this.getIndicatorValue(d, indicator));
      dataByIndicator.set(indicator, values);
    });

    // Create datasets using the multi-indicator manager (no norm data for UGM daily)
    const datasets = this.multiIndicatorChartDataManager.createMultiIndicatorDatasets(
      this.selectedIndicators,
      dataByIndicator
    );

    this.chartData = {labels: dateLabels, datasets};
    this.configureMultiAxisChart();
  }

  /**
   * Process multi-indicator IBM chart data
   */
  private processMultiIndicatorIbmChartData(monthlyData: any[], normalizedData: any[] | null): void {
    if (!monthlyData || monthlyData.length === 0 || this.selectedIndicators.length === 0) {
      this.chartData = {labels: [], datasets: []};
      this.calculateYAxisValues();
      return;
    }

    const dataByMonth = this.ibmDataManager.processMonthlyDataByMonth(monthlyData);
    const selectedYear = parseInt(this.selectedPeriod);
    const maxMonth = this.ibmDataManager.getMaxMonth(selectedYear);
    const monthNames = this.getTranslatedMonthNames();
    const labels = monthNames.slice(0, maxMonth);

    // Extract values for each indicator
    const dataByIndicator = new Map<string, (number | null)[]>();
    this.selectedIndicators.forEach(indicator => {
      const monthlyValues = this.ibmDataManager.extractMonthlyValues(dataByMonth, maxMonth, indicator);
      dataByIndicator.set(indicator, monthlyValues);
    });

    let normDataByIndicator: Map<string, (number | null)[]> = new Map();
    if (normalizedData && normalizedData.length > 0) {
      this.selectedIndicators.forEach(indicator => {
        const normDataByMonth = this.aggregateNormDataByMonth(normalizedData, indicator);
        const normValues: (number | null)[] = [];
        for (let month = 1; month <= maxMonth; month++) {
          const normMonth = normDataByMonth.get(month);
          if (normMonth) {
            const normValue = normMonth.average !== undefined
              ? normMonth.average
              : this.getIndicatorValue(normMonth, indicator);
            normValues.push(normValue);
          } else {
            normValues.push(null);
          }
        }
        normDataByIndicator.set(indicator, normValues);
      });
    }

    // Create datasets using IBM data + ERA norms
    const datasets = this.multiIndicatorChartDataManager.createMultiIndicatorDatasets(
      this.selectedIndicators,
      dataByIndicator,
      normDataByIndicator
    );

    this.chartData = {labels, datasets};
    this.configureMultiAxisChart();
  }

  /**
   * Process multi-indicator multi-year ERA5 data
   */
  private processMultiIndicatorMultiYearData(yearDataArrays: any[][], normalizedData: any[] | null): void {
    if (!this.selectedIndicators || this.selectedIndicators.length === 0) {
      this.chartData = {labels: [], datasets: []};
      return;
    }

    const monthNames = this.getTranslatedMonthNames();
    const labels = monthNames.slice(0, 12);

    // Build year values arrays for each indicator
    const yearValuesByIndicator = new Map<string, (number | null)[][]>();

    this.selectedIndicators.forEach(indicator => {
      const yearValuesForIndicator: (number | null)[][] = [];

      this.selectedYears.forEach((year, yearIndex) => {
        const yearData = yearDataArrays[yearIndex] || [];
        const values = this.multiYearDataManager.processMonthlyData(
          yearData,
          12,
          (data) => this.getIndicatorValue(data, indicator),
          indicator
        );
        yearValuesForIndicator.push(values);
      });

      yearValuesByIndicator.set(indicator, yearValuesForIndicator);
    });

    // Extract normalized data for each indicator
    let normDataByIndicator: Map<string, (number | null)[]> = new Map();
    if (normalizedData && normalizedData.length > 0) {
      normDataByIndicator = new Map();

      this.selectedIndicators.forEach(indicator => {
        const normDataByMonth = this.aggregateNormDataByMonth(normalizedData, indicator);
        const normValues: (number | null)[] = [];
        for (let month = 1; month <= 12; month++) {
          const normMonth = normDataByMonth.get(month);
          if (normMonth) {
            const normValue = normMonth.average !== undefined
              ? normMonth.average
              : this.getIndicatorValue(normMonth, indicator);
            normValues.push(normValue);
          } else {
            normValues.push(null);
          }
        }
        normDataByIndicator.set(indicator, normValues);
      });
    }

    // Create datasets for all indicators and years
    const datasets: ChartDataset[] = [];

    this.selectedIndicators.forEach((indicator, indicatorIndex) => {
      const yAxisID = indicatorIndex === 0 ? 'y' : 'y1';
      const yearValues = yearValuesByIndicator.get(indicator) || [];

      // Add dataset for each year with different colors per year
      this.selectedYears.forEach((year, yearIndex) => {
        const values = yearValues[yearIndex] || [];
        const yearColor = this.indicatorManager.getColorByIndicatorAndYear(indicator, yearIndex);

        // Create dataset with year-specific color
        const dataset: ChartDataset = {
          label: `${this.translate.instant(indicator)} (${year})`,
          data: values,
          borderColor: yearColor,
          backgroundColor: yearColor + '33',
          borderWidth: 2,
          yAxisID: yAxisID
        };

        datasets.push(dataset);
      });

      // Add normalized data for this indicator if available
      const normValues = normDataByIndicator.get(indicator);
      if (normValues && normValues.length > 0) {
        const normDataset = this.weatherNormDataManager.createNormDataset(
          normValues,
          indicator,
          this.translate.instant(indicator),
          this.getIndicatorColor(indicator)
        );
        normDataset.yAxisID = yAxisID;
        datasets.push(normDataset);
      }
    });

    this.chartData = {labels, datasets};
    this.configureMultiAxisChart();
  }  /**
   * Process multi-indicator multi-year IBM data
   */
  private processMultiIndicatorMultiYearIbmData(yearDataArrays: any[][], normalizedData: any[] | null): void {
    if (!this.selectedIndicators || this.selectedIndicators.length === 0) {
      this.chartData = {labels: [], datasets: []};
      return;
    }

    const monthNames = this.getTranslatedMonthNames();
    const labels = monthNames.slice(0, 12);
    const datasets: ChartDataset[] = [];
    const normDataByIndicator: Map<string, (number | null)[]> = new Map();

    if (normalizedData && normalizedData.length > 0) {
      this.selectedIndicators.forEach(indicator => {
        const normDataByMonth = this.aggregateNormDataByMonth(normalizedData, indicator);
        const normValues: (number | null)[] = [];
        for (let month = 1; month <= 12; month++) {
          const normMonth = normDataByMonth.get(month);
          if (normMonth) {
            const normValue = normMonth.average !== undefined
              ? normMonth.average
              : this.getIndicatorValue(normMonth, indicator);
            normValues.push(normValue);
          } else {
            normValues.push(null);
          }
        }
        normDataByIndicator.set(indicator, normValues);
      });
    }

    // For each selected indicator
    this.selectedIndicators.forEach((indicator, indicatorIndex) => {
      const yAxisID = indicatorIndex === 0 ? 'y' : 'y1';

      // Add dataset for each selected year
      this.selectedYears.forEach((year, yearIndex) => {
        const yearData = yearDataArrays[yearIndex] || [];
        const values = this.multiYearDataManager.processMonthlyData(
          yearData,
          12,
          (data) => this.getIndicatorValue(data, indicator),
          indicator
        );

        // Use indicator and year-based color
        const yearColor = this.indicatorManager.getColorByIndicatorAndYear(indicator, yearIndex);

        const dataset: ChartDataset = {
          label: `${this.translate.instant(indicator)} (${year})`,
          data: values,
          borderColor: yearColor,
          backgroundColor: yearColor + '33',
          borderWidth: 2,
          yAxisID: yAxisID
        };

        datasets.push(dataset);
      });

      const normValues = normDataByIndicator.get(indicator);
      if (normValues && normValues.length > 0) {
        const normDataset = this.weatherNormDataManager.createNormDataset(
          normValues,
          indicator,
          this.translate.instant(indicator),
          this.getIndicatorColor(indicator)
        );
        normDataset.yAxisID = yAxisID;
        datasets.push(normDataset);
      }
    });

    this.chartData = {labels, datasets};
    this.configureMultiAxisChart();
  }

  /**
   * Configure chart with multiple Y axes (dual axis setup)
   */
  private configureMultiAxisChart(): void {
    if (this.selectedIndicators.length <= 1) {
      // Single indicator - use normal axis configuration
      this.resetToSingleAxisConfig();
      return;
    }

    // Multiple indicators - configure dual Y axes
    const datasets = this.chartData.datasets;
    if (!datasets || datasets.length === 0) {
      this.resetToSingleAxisConfig();
      return;
    }

    // Get values for each Y axis (excluding norm data which has different styling)
    const yAxisData = datasets
      .filter(d => d.yAxisID === 'y' && !d.label?.includes('(Norm)'))
      .flatMap(d => d.data)
      .filter(v => v !== null && v !== undefined) as number[];

    const y1AxisData = datasets
      .filter(d => d.yAxisID === 'y1' && !d.label?.includes('(Norm)'))
      .flatMap(d => d.data)
      .filter(v => v !== null && v !== undefined) as number[];

    // If y1 has no data, switch back to single axis configuration
    if (y1AxisData.length === 0) {
      this.resetToSingleAxisConfig();
      return;
    }

    // Get norm data values if they exist to include in scale calculation
    const yAxisNormData = datasets
      .filter(d => d.yAxisID === 'y' && d.label?.includes('(Norm)'))
      .flatMap(d => d.data)
      .filter(v => v !== null && v !== undefined) as number[];

    const y1AxisNormData = datasets
      .filter(d => d.yAxisID === 'y1' && d.label?.includes('(Norm)'))
      .flatMap(d => d.data)
      .filter(v => v !== null && v !== undefined) as number[];

    // Combine data and norm for scale calculation
    const yAxisAllData = [...yAxisData, ...yAxisNormData];
    const y1AxisAllData = [...y1AxisData, ...y1AxisNormData];

    // Use manager to create dual axis config with units
    const scales = this.multiIndicatorChartDataManager.createDualAxisConfig(
      this.selectedIndicators,
      yAxisAllData,
      y1AxisAllData,
      this.getUnitForIndicator(this.selectedIndicators[0]),
      this.selectedIndicators.length > 1 ? this.getUnitForIndicator(this.selectedIndicators[1]) : ''
    );

    // Update chart config with dual axes
    const yUnit = this.getUnitForIndicator(this.selectedIndicators[0]);
    const y1Unit = this.selectedIndicators.length > 1 ? this.getUnitForIndicator(this.selectedIndicators[1]) : '';

    this.chartConfig = {
      ...this.chartConfig,
      scales,
      yAxisUnit: `${this.translate.instant(this.selectedIndicators[0])}${yUnit ? ' (' + yUnit + ')' : ''}`,
      y1AxisUnit: this.selectedIndicators.length > 1 ? `${this.translate.instant(this.selectedIndicators[1])}${y1Unit ? ' (' + y1Unit + ')' : ''}` : ''
    };

    this.yAxisValues = this.extractYAxisValues(scales.y);
  }

  /**
   * Reset chart configuration to single axis (removes Y1 axis)
   */
  private resetToSingleAxisConfig(): void {
    // Create single axis configuration without Y1
    const indicator = this.selectedIndicators[0] || this.selectedIndicator;
    const yUnit = this.getUnitForIndicator(indicator);
    const yAxisTitle = `${this.translate.instant(indicator)}${yUnit ? ' (' + yUnit + ')' : ''}`;

    // Get Y axis data to calculate scale
    const yAxisData = this.chartData.datasets
      .filter(d => !d.label?.includes('(Norm)'))
      .flatMap(d => d.data)
      .filter(v => v !== null && v !== undefined) as number[];

    // Calculate scale using simple logic
    let min = 0;
    let max = 100;
    if (yAxisData.length > 0) {
      min = Math.min(...yAxisData);
      let maxVal = Math.max(...yAxisData);
      if (min > 0) min = 0;
      const range = maxVal - min;
      const step = range / 5;
      max = maxVal + (step * 0.1); // Add 10% padding
    }

    this.chartConfig = {
      showGridLines: true,
      yAxisUnit: yAxisTitle,
      y1AxisUnit: '',
      viewBox: {width: 800, height: 500},
      padding: {top: 50, right: 50, bottom: 100, left: 50},
      chartHeight: 350,
      gridLineCount: 13,
      lineStrokeWidth: 2,
      scales: {
        x: {
          stacked: false
        },
        y: {
          stacked: false,
          min: min,
          max: max,
          title: {
            display: true,
            text: yAxisTitle
          }
        }
      }
    };
    this.calculateYAxisValues();
  }

  /**
   * Extract Y axis values from scale configuration
   */
  private extractYAxisValues(yAxis: any): number[] {
    if (yAxis && typeof yAxis.max !== 'undefined' && typeof yAxis.min !== 'undefined') {
      const min = yAxis.min;
      const max = yAxis.max;
      const step = (max - min) / 5;
      return Array.from({length: 6}, (_, i) => max - (i * step));
    }
    return [0, 20, 40, 60, 80, 100];
  }
}
